import PropTypes from 'prop-types';
import {
  Box, Typography, Switch,
  TextField, FormControlLabel, Button, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Stack, Alert,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import StandardCard from '../../components/ui/StandardCard';
import SettingsSection from '../../components/ui/SettingsSection';
import SettingRow from '../../components/ui/SettingRow';
import FormActionBar from '../../components/ui/FormActionBar';
import { brandColors } from '../../constants/brandColors';

function clampHour(v, fallback) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(24, Math.max(0, n));
}

/**
 * WhatsAppSettingsCard — Card de configurações WhatsApp.
 * Recebe form state + handlers.
 */
export default function WhatsAppSettingsCard({
  loadingSettings,
  savingSettings,
  form,
  updateField,
  handleSave,
  models,
}) {
  const bStart = clampHour(form.reminderBusinessStart, 8);
  const bEnd = clampHour(form.reminderBusinessEnd, 18);

  return (
    <StandardCard sx={{ mb: 3 }}>
      {/* Seção 1: Habilitar WhatsApp */}
      <SettingsSection
        icon={<WhatsAppIcon sx={{ color: brandColors.whatsapp.main, fontSize: 20 }} />}
        title="WhatsApp"
        description="Quando habilitado, seus clientes recebem mensagens de agendamento pelo WhatsApp."
        action={
          loadingSettings ? (
            <CircularProgress size={20} />
          ) : (
            <Switch
              checked={form.enabled}
              onChange={(e) => updateField('enabled', e.target.checked)}
              color="success"
            />
          )
        }
      >
        {/* Placeholder — seção interna mostrada apenas quando enabled */}
      </SettingsSection>

      {!loadingSettings && form.enabled && (
        <>
          {/* Seção 2: Mensagem ao cliente */}
          <SettingsSection title="Mensagem ao cliente">
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel id="wa-style-label">Modelo da mensagem</InputLabel>
              <Select
                labelId="wa-style-label"
                label="Modelo da mensagem"
                value={form.templateStyle}
                onChange={(e) => updateField('templateStyle', Number(e.target.value))}
              >
                {(models.length ? models : [{ id: 1, name: 'Modelo 1' }, { id: 2, name: 'Modelo 2' }]).map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Orientações ao cliente (opcional)"
              value={form.messageNotes}
              onChange={(e) => updateField('messageNotes', e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              helperText="Aparece nas mensagens ao cliente. Ex.: vir em jejum, trazer documento, chegar 10min antes."
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', lg: 'block' }, mt: 2 }}>
              Veja a pré-visualização ao lado →
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', lg: 'none' }, mt: 2 }}>
              Veja a pré-visualização abaixo ↓
            </Typography>
          </SettingsSection>

          {/* Seção 3: Avisar na criação */}
          <SettingsSection title="Avisar o cliente ao criar o agendamento">
            <SettingRow
              label="Quando o agendamento vem do site público"
              control={
                <Switch
                  checked={form.notifyOnPublicCreate}
                  onChange={(e) => updateField('notifyOnPublicCreate', e.target.checked)}
                  color="success"
                />
              }
            />
            <SettingRow
              label="Quando você cria o agendamento aqui no painel"
              control={
                <Switch
                  checked={form.notifyOnPrivateCreate}
                  onChange={(e) => updateField('notifyOnPrivateCreate', e.target.checked)}
                  color="success"
                />
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Se desligar, não enviamos a mensagem de criação — apenas o lembrete (se ativo).
              Ao criar um agendamento no painel você ainda pode forçar o envio caso a caso.
            </Typography>
          </SettingsSection>

          {/* Seção 4: Lembrete */}
          <SettingsSection
            title="Lembrete"
            description="O lembrete consome cota de mensagens e traz os botões Confirmar / Cancelar."
            action={
              <Switch
                checked={form.reminderEnabled}
                onChange={(e) => updateField('reminderEnabled', e.target.checked)}
                color="success"
              />
            }
          >
            {form.reminderEnabled && (
              <Stack spacing={2}>
                <SettingRow
                  label="Horas antes"
                  control={
                    <TextField
                      type="number"
                      size="small"
                      value={form.reminderHoursBefore}
                      onChange={(e) => updateField('reminderHoursBefore', e.target.value)}
                      inputProps={{ min: 1, max: 168 }}
                      sx={{ width: 100 }}
                    />
                  }
                />

                <SettingRow
                  label="Respeitar horário comercial"
                  control={
                    <Switch
                      checked={form.reminderRespectBusinessHours}
                      onChange={(e) => updateField('reminderRespectBusinessHours', e.target.checked)}
                      color="success"
                    />
                  }
                />

                {form.reminderRespectBusinessHours && (
                  <Box sx={{ pl: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <TextField
                        label="Início"
                        type="number"
                        size="small"
                        value={form.reminderBusinessStart}
                        onChange={(e) => updateField('reminderBusinessStart', e.target.value)}
                        inputProps={{ min: 0, max: 24 }}
                        sx={{ width: 80 }}
                      />
                      <Typography variant="body2" color="text.secondary">às</Typography>
                      <TextField
                        label="Fim"
                        type="number"
                        size="small"
                        value={form.reminderBusinessEnd}
                        onChange={(e) => updateField('reminderBusinessEnd', e.target.value)}
                        inputProps={{ min: 0, max: 24 }}
                        sx={{ width: 80 }}
                      />
                    </Stack>
                  </Box>
                )}

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Se o horário do lembrete cair fora do expediente, ele é deslocado para dentro dele.
                    Exemplos (expediente {bStart}h–{bEnd}h):
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Agendamento às 14h, lembrete 3h antes → 11h → enviado <b>11h do mesmo dia</b>.</li>
                    <li>Agendamento ao meio-dia, lembrete 12h antes → 00h → enviado <b>{bEnd}h do dia anterior</b>.</li>
                    <li>Agendamento às 20h, lembrete 1h antes → 19h → enviado <b>{bEnd}h do mesmo dia</b>.</li>
                  </Box>
                </Alert>
              </Stack>
            )}
          </SettingsSection>

          {/* Seção 5: Notificações pro dono */}
          <SettingsSection
            title="Notificações pra você (dono)"
            action={
              <Switch
                checked={form.ownerNotifyEnabled}
                onChange={(e) => updateField('ownerNotifyEnabled', e.target.checked)}
                color="success"
              />
            }
          >
            {form.ownerNotifyEnabled && (
              <Stack spacing={2}>
                <TextField
                  label="Meu WhatsApp (com DDD)"
                  value={form.ownerPhone}
                  onChange={(e) => updateField('ownerPhone', e.target.value)}
                  placeholder="(31) 99999-9999"
                  inputProps={{ inputMode: 'tel', maxLength: 20 }}
                  fullWidth
                />
                <SettingRow
                  label="Novo agendamento"
                  control={
                    <Switch
                      checked={form.ownerNotifyOnCreate}
                      onChange={(e) => updateField('ownerNotifyOnCreate', e.target.checked)}
                      color="success"
                    />
                  }
                />
                <SettingRow
                  label="Cliente confirmou presença"
                  control={
                    <Switch
                      checked={form.ownerNotifyOnConfirm}
                      onChange={(e) => updateField('ownerNotifyOnConfirm', e.target.checked)}
                      color="success"
                    />
                  }
                />
                <SettingRow
                  label="Cliente cancelou/desistiu"
                  control={
                    <Switch
                      checked={form.ownerNotifyOnCancel}
                      onChange={(e) => updateField('ownerNotifyOnCancel', e.target.checked)}
                      color="success"
                    />
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  Cada notificação enviada ao seu WhatsApp consome 1 crédito de mensagem.
                </Typography>
              </Stack>
            )}
          </SettingsSection>
        </>
      )}

      {/* Salvar sempre visível quando carregado — inclusive p/ persistir o WhatsApp DESABILITADO. */}
      {!loadingSettings && (
        <FormActionBar>
          <Button
            variant="contained"
            color="success"
            onClick={handleSave}
            disabled={savingSettings}
            startIcon={savingSettings ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Salvar configurações
          </Button>
        </FormActionBar>
      )}
    </StandardCard>
  );
}

WhatsAppSettingsCard.propTypes = {
  loadingSettings: PropTypes.bool.isRequired,
  savingSettings: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  models: PropTypes.array.isRequired,
};
